import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FaUser, FaCalendar, FaClock, FaPlus, FaEdit, FaCheck, FaTimes, FaArrowRight, FaTrash, FaPaperPlane, FaPaperclip, FaSearch } from 'react-icons/fa';
import { getFireById, updateFire, addNoteToFire, updateNoteInFire, deleteNoteFromFire, deleteFire } from '../services/firebase/fires';
import { getInternalUsers } from '../services/userService';
import { getAccountContacts } from '../services/firebase/users';
import { getAccounts } from '../services/firestoreService';
import BackButton from '../components/BackButton';
import SaveButton from '../components/SaveButton';
import { useAuth } from '../contexts/AuthContext';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import BBALoader from '../components/BBALoader';

const FireOverview = () => {
  const { fireId } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [fire, setFire] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newAssignee, setNewAssignee] = useState('');
  const [newNote, setNewNote] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editedFire, setEditedFire] = useState(null);
  const [editingNote, setEditingNote] = useState(null);
  const [editedNoteText, setEditedNoteText] = useState('');
  const [internalUsers, setInternalUsers] = useState([]);
  const [accountContacts, setAccountContacts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [currentUserProfile, setCurrentUserProfile] = useState(null);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [isEditingFire, setIsEditingFire] = useState(false);
  const [clientAccounts, setClientAccounts] = useState([]);
  const [clientSearch, setClientSearch] = useState('');
  const [availableAssignees, setAvailableAssignees] = useState([]);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [fireData, usersData, accountsData] = await Promise.all([
          getFireById(fireId),
          getInternalUsers(),
          getAccounts()
        ]);
        
        setFire({
          ...fireData,
          notes: Array.isArray(fireData.notes) ? fireData.notes : []
        });
        setInternalUsers(usersData);
        
        // Set client accounts (filter out internal accounts and get unique company names)
        const uniqueCompanies = [...new Set(accountsData.map(account => account.company).filter(Boolean))];
        setClientAccounts(uniqueCompanies);
        
        // Initialize edited fire with current fire data
        setEditedFire({
          ...fireData,
          notes: Array.isArray(fireData.notes) ? fireData.notes : []
        });
        
        setLoading(false);
      } catch (error) {
        console.error('Error loading data:', error);
        setLoading(false);
      }
    };

    loadData();
  }, [fireId]);

  // Load account contacts when account changes
  useEffect(() => {
    const loadAccountContacts = async () => {
      if (editedFire?.companyName && editedFire.companyName !== 'Internal') {
        try {
          const contacts = await getAccountContacts(editedFire.companyName);
          setAccountContacts(contacts);
        } catch (error) {
          console.error('Error loading account contacts:', error);
        }
      } else {
        setAccountContacts([]);
      }
    };

    loadAccountContacts();
  }, [editedFire?.companyName]);

  // Get unique accounts from internal users
  useEffect(() => {
    const uniqueAccounts = [...new Set(internalUsers.map(user => user.company))];
    setAccounts(['Internal', ...uniqueAccounts]);
  }, [internalUsers]);

  // Update available assignees when internal users or account contacts change
  useEffect(() => {
    const assignees = [
      ...internalUsers.map(user => ({
        id: user.id,
        name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unknown User',
        type: 'Internal'
      })),
      ...accountContacts.map(contact => ({
        id: contact.id,
        name: contact.name || contact.displayName || contact.email || 'Unknown Contact',
        type: 'Contact'
      }))
    ];
    setAvailableAssignees(assignees);
  }, [internalUsers, accountContacts]);

  // Load current user profile
  useEffect(() => {
    const loadCurrentUserProfile = async () => {
      if (!currentUser) return;
      
      try {
        // Try to get from users collection first (portal users)
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const userData = userDoc.data();
          const profileImage = userData.profileImage || currentUser.photoURL;
          
          setCurrentUserProfile({
            id: currentUser.uid,
            name: `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || userData.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
            profileImage: profileImage,
            role: userData.role || 'client'
          });
          return;
        }
        
        // If not found, try internal_users collection (CRM users)
        const internalUserDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        if (internalUserDoc.exists()) {
          const internalUserData = internalUserDoc.data();
          const profileImage = internalUserData.profileImage || currentUser.photoURL;
          
          setCurrentUserProfile({
            id: currentUser.uid,
            name: `${internalUserData.firstName || ''} ${internalUserData.lastName || ''}`.trim() || internalUserData.displayName || currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
            profileImage: profileImage,
            role: internalUserData.role || 'internal'
          });
        }
      } catch (error) {
        console.error('Error loading current user profile:', error);
        // Fallback to basic user info
        setCurrentUserProfile({
          id: currentUser.uid,
          name: currentUser.displayName || currentUser.email?.split('@')[0] || 'User',
          profileImage: currentUser.photoURL,
          role: 'user'
        });
      }
    };

    loadCurrentUserProfile();
  }, [currentUser]);

  // Function to check if image URL is accessible
  const isImageAccessible = (url) => {
    if (!url) return false;
    
    // Google profile images often have CORS issues, so we'll show fallback for them
    if (url.includes('googleusercontent.com') || url.includes('lh3.googleusercontent.com')) {
      return false;
    }
    
    return true;
  };

  // Filter client accounts based on search
  const filteredClientAccounts = clientAccounts.filter(company =>
    company.toLowerCase().includes(clientSearch.toLowerCase())
  );

  // Handle fire editing
  const handleEditFire = () => {
    setIsEditingFire(true);
    setEditedFire({ ...fire });
  };

  const handleCancelEdit = () => {
    setIsEditingFire(false);
    setEditedFire({ ...fire });
    setClientSearch('');
  };

  const handleSaveFire = async () => {
    try {
      const updatedFire = await updateFire(fire.id, {
        name: editedFire.name,
        description: editedFire.description,
        companyName: editedFire.companyName,
        assignedTo: editedFire.assignedTo
      });
      setFire(prev => ({ ...prev, ...updatedFire }));
      setIsEditingFire(false);
      setClientSearch('');
    } catch (error) {
      console.error('Error updating fire:', error);
      alert('Failed to update fire. Please try again.');
    }
  };

  const handleFireFieldChange = (field, value) => {
    setEditedFire(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleAssigneeChange = (assigneeName, checked) => {
    setEditedFire(prev => ({
      ...prev,
      assignedTo: checked
        ? [...(prev.assignedTo || []), assigneeName]
        : (prev.assignedTo || []).filter(name => name !== assigneeName)
    }));
  };

  const addAssignee = async () => {
    if (newAssignee && !(fire.assignedTo || []).includes(newAssignee)) {
      try {
        const updatedFire = await updateFire(fire.id, {
          assignedTo: [...(fire.assignedTo || []), newAssignee]
        });
        setFire(prev => ({ ...prev, ...updatedFire }));
        setNewAssignee('');
      } catch (error) {
        console.error('Error adding assignee:', error);
      }
    }
  };

  const addNote = async () => {
    if (newNote && currentUserProfile) {
      try {
        setSendingMessage(true);
        const newNoteData = await addNoteToFire(fire.id, {
          noteText: newNote,
          author: currentUserProfile.name,
          authorId: currentUserProfile.id,
          authorRole: currentUserProfile.role
        });
        setFire(prev => ({
          ...prev,
          notes: Array.isArray(prev.notes) ? [...prev.notes, newNoteData] : [newNoteData]
        }));
        setNewNote('');
      } catch (error) {
        console.error('Error adding note:', error);
        alert('Failed to send message. Please try again.');
      } finally {
        setSendingMessage(false);
      }
    }
  };

  // Handle status update
  const handleStatusUpdate = async (newStatus) => {
    if (!fire || newStatus === fire.status) return;
    
    try {
      const updateData = { status: newStatus };
      
      // If marking as resolved, add the resolved date
      if (newStatus === 'Resolved') {
        updateData.dateResolved = new Date().toISOString();
      }
      
      // Update the fire status in Firebase
      const updatedFire = await updateFire(fire.id, updateData);
      setFire(prev => ({ ...prev, ...updatedFire }));
      
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status. Please try again.');
    }
  };

  const editNote = (index) => {
    setEditingNote(index);
    setEditedNoteText(fire.notes[index].noteText);
  };

  const saveNote = async (index) => {
    try {
      const updatedNote = await updateNoteInFire(fire.id, fire.notes[index].id, editedNoteText);
      setFire(prev => ({
        ...prev,
        notes: prev.notes.map((note, i) => i === index ? updatedNote : note)
      }));
      setEditingNote(null);
      setEditedNoteText('');
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const cancelNoteEdit = () => {
    setEditingNote(null);
    setEditedNoteText('');
  };

  const deleteNote = async (index) => {
    try {
      const noteToDelete = fire.notes[index];
      await deleteNoteFromFire(fire.id, noteToDelete.id);
      setFire(prev => ({
        ...prev,
        notes: prev.notes.filter((_, i) => i !== index)
      }));
    } catch (error) {
      console.error('Error deleting note:', error);
      alert('Failed to delete note. Please try again.');
    }
  };

  const removeAssignee = async (assigneeToRemove) => {
    try {
      const updatedFire = await updateFire(fire.id, {
        assignedTo: (fire.assignedTo || []).filter(assignee => assignee !== assigneeToRemove)
      });
      setFire(prev => ({ ...prev, ...updatedFire }));
    } catch (error) {
      console.error('Error removing assignee:', error);
    }
  };

  // Handle deleting fire
  const handleDeleteFire = async () => {
    if (!fire) return;
    
    const confirmDelete = window.confirm(
      `Are you sure you want to delete this fire?\n\n"${fire.name}"\n\nThis action cannot be undone and will also delete all associated notes.`
    );
    
    if (!confirmDelete) return;
    
    try {
      setIsDeleting(true);
      await deleteFire(fire.id);
      navigate('/crm/fires');
    } catch (error) {
      console.error('Error deleting fire:', error);
      alert('Failed to delete fire. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-6 min-h-screen relative">
        <PortalBackground />
        <div className="relative flex items-center justify-center h-full">
          <BBALoader size={48} className="mx-auto" />
        </div>
      </div>
    );
  }

  if (!fire) {
    return (
      <div className="container mx-auto px-4 py-6 min-h-screen relative">
        <PortalBackground />
        <div className="relative text-center">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Fire not found</h1>
          <Link to="/crm/fires" className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 mt-4 inline-block">
            Return to Fires
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 min-h-screen relative">
      {/* Header */}
      <PortalBackground />
      <div className="relative">
      <div className="relative mb-6">
        {/* Back Button - Positioned absolutely on the left */}
        <div className="absolute left-0 top-0 z-10">
          <BackButton onClick={() => navigate('/crm/fires')} />
        </div>
        
        {/* Action Buttons - Positioned absolutely on the right */}
        <div className="absolute right-0 top-0 z-10 flex items-center gap-2">
          {!isEditingFire ? (
            <button
              onClick={handleEditFire}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
            >
              <FaEdit className="h-4 w-4" />
              Edit Fire
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSaveFire}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
              >
                <FaCheck className="h-4 w-4" />
                Save
              </button>
              <button
                onClick={handleCancelEdit}
                className="flex items-center gap-2 px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg font-medium transition-colors"
              >
                <FaTimes className="h-4 w-4" />
                Cancel
              </button>
            </div>
          )}
          <button
            onClick={handleDeleteFire}
            disabled={isDeleting}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 disabled:bg-red-400 text-white rounded-lg font-medium transition-colors"
          >
            <FaTrash className="h-4 w-4" />
            {isDeleting ? 'Deleting...' : 'Delete'}
          </button>
        </div>
        
        {/* Centered Content */}
        <div className="flex flex-col items-center justify-center min-h-[80px]">
          <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-gray-100">{fire.name}</h1>
          <span className={`inline-block mt-2 px-3 py-1 rounded-full text-sm font-semibold
            ${fire.status === 'Active' ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200' : 
              fire.status === 'Pending' ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200' :
              'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'}`}>
            {fire.status}
          </span>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {/* This block is removed as the delete button was removed */}

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Description and Details */}
        <div className="lg:col-span-1 space-y-6">
          {/* Fire Information Card */}
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-6`}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4 text-center">Fire Information</h2>
            <div className="space-y-4">
              {/* Fire Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Fire Name
                </label>
                {isEditingFire ? (
                  <input
                    type="text"
                    value={editedFire.name || ''}
                    onChange={(e) => handleFireFieldChange('name', e.target.value)}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                ) : (
                  <p className="text-sm text-gray-900 dark:text-gray-100">{fire.name}</p>
                )}
              </div>

              {/* Account */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Account
                </label>
                {isEditingFire ? (
                  <div>
                    <select
                      value={editedFire.companyName || ''}
                      onChange={(e) => handleFireFieldChange('companyName', e.target.value)}
                      className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    >
                      <option value="">Select account</option>
                      <option value="Internal">Internal</option>
                    </select>
                    
                    {/* Client Search */}
                    {editedFire.companyName !== 'Internal' && (
                      <div className="mt-2">
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <FaSearch className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                          </div>
                          <input
                            type="text"
                            placeholder="Search clients..."
                            className="w-full pl-10 pr-10 p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-sm"
                            value={clientSearch}
                            onChange={(e) => setClientSearch(e.target.value)}
                          />
                          {clientSearch && (
                            <button
                              type="button"
                              onClick={() => setClientSearch('')}
                              className="absolute inset-y-0 right-0 pr-3 flex items-center"
                            >
                              <FaTimes className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
                            </button>
                          )}
                        </div>
                        
                        {/* Client List */}
                        <div className="mt-2 max-h-32 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700">
                          {filteredClientAccounts.length > 0 ? (
                            filteredClientAccounts.map((company, idx) => (
                              <button
                                key={idx}
                                type="button"
                                onClick={() => {
                                  handleFireFieldChange('companyName', company);
                                  setClientSearch('');
                                }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                              >
                                {company}
                              </button>
                            ))
                          ) : (
                            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                              {clientSearch ? 'No clients found' : 'No clients available'}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 dark:text-gray-100">{fire.companyName}</p>
                )}
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Status
                </label>
                <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-full ${
                  fire.status === 'Active' ? 'bg-red-100 dark:bg-red-900' :
                  fire.status === 'Pending' ? 'bg-yellow-100 dark:bg-yellow-900' :
                  'bg-green-100 dark:bg-green-900'
                }`}>
                  <span className={`text-sm font-medium ${
                    fire.status === 'Active' ? 'text-red-800 dark:text-red-200' :
                    fire.status === 'Pending' ? 'text-yellow-800 dark:text-yellow-200' :
                    'text-green-800 dark:text-green-200'
                  }`}>
                    {fire.status}
                  </span>
                </div>
              </div>

              {/* Assigned To */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Assigned To
                </label>
                {isEditingFire ? (
                  <div className="border border-gray-300 dark:border-gray-600 rounded p-2 max-h-32 overflow-y-auto bg-white dark:bg-gray-700">
                    {availableAssignees.length > 0 ? (
                      availableAssignees.map((assignee) => (
                        <div key={assignee.id} className="flex items-center space-x-2 py-1">
                          <input
                            type="checkbox"
                            id={`assignee-${assignee.id}`}
                            checked={(editedFire.assignedTo || []).includes(assignee.name)}
                            onChange={(e) => handleAssigneeChange(assignee.name, e.target.checked)}
                            className="h-4 w-4 text-red-600 border-gray-300 dark:border-gray-600 rounded"
                          />
                          <label htmlFor={`assignee-${assignee.id}`} className="text-sm text-gray-700 dark:text-gray-300 flex items-center">
                            {assignee.name}
                            <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({assignee.type})</span>
                          </label>
                        </div>
                      ))
                    ) : (
                      <div className="text-sm text-gray-500 dark:text-gray-400 py-2">
                        No assignees available
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {(fire.assignedTo || []).map((assignee, index) => (
                      <div key={index} className="flex items-center justify-between text-sm text-gray-900 dark:text-gray-100">
                        <div className="flex items-center">
                          <FaUser className="mr-2 text-gray-400" />
                          {assignee}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date Reported
                </label>
                <p className="text-sm text-gray-900 dark:text-gray-100">
                  {new Date(fire.dateReported).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>

              {fire.status === 'Resolved' && fire.dateResolved && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Date Resolved
                  </label>
                  <p className="text-sm text-gray-900 dark:text-gray-100">
                    {new Date(fire.dateResolved).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              )}

              {/* Status Update Buttons */}
              {fire.status === 'Active' && (
                <div className="pt-4">
                  <button
                    onClick={() => handleStatusUpdate('Pending')}
                    className="w-full px-4 py-2 bg-yellow-600 hover:bg-yellow-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Mark As In Progress
                  </button>
                </div>
              )}
              {fire.status === 'Pending' && (
                <div className="pt-4">
                  <button
                    onClick={() => handleStatusUpdate('Resolved')}
                    className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Mark as Resolved
                  </button>
                </div>
              )}
              {fire.status === 'Resolved' && (
                <div className="pt-4">
                  <button
                    onClick={() => handleStatusUpdate('Active')}
                    className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                  >
                    Mark as Open
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Column - Messaging Interface */}
        <div className="lg:col-span-2">
          <div className={`bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm`}>
            {/* Description Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">Description</h3>
              {isEditingFire ? (
                <textarea
                  value={editedFire.description || ''}
                  onChange={(e) => handleFireFieldChange('description', e.target.value)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows="4"
                  placeholder="Enter fire description"
                />
              ) : (
                <p className="text-gray-700 dark:text-gray-300">{fire.description}</p>
              )}
            </div>

            {/* Messages List */}
            <div className="p-6 max-h-96 overflow-y-auto">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Messages</h3>
             
              {(fire.notes || []).length === 0 ? (
                <p className="text-gray-500 dark:text-gray-400 text-center py-8">No messages yet</p>
              ) : (
                <div className="space-y-4">
                  {(fire.notes || []).map((note, index) => {
                    const isCurrentUser = note && note.authorId === currentUserProfile?.id;
                    const authorName = typeof note?.author === 'string'
                      ? note.author
                      : (note?.author?.name || note?.author?.displayName || note?.author?.email || 'User');
                    const noteText = typeof note?.noteText === 'string'
                      ? note.noteText
                      : (typeof note === 'string' ? note : '');
                    const otherUserProfileImage = note?.authorProfileImage
                      || internalUsers.find(u => u.id === note?.authorId)?.profileImage
                      || null;
                    return (
                      <div key={index} className={`flex gap-3 ${isCurrentUser ? 'flex-row-reverse' : ''}`}>
                        <div className="flex-shrink-0">
                          {isCurrentUser ? (
                            // Current user's message - show their profile picture or fallback
                            <div className="relative">
                              {currentUserProfile?.profileImage && isImageAccessible(currentUserProfile.profileImage) ? (
                                <img 
                                  src={currentUserProfile.profileImage} 
                                  alt={currentUserProfile.name}
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              {/* Always show fallback avatar */}
                              <div 
                                className={`w-8 h-8 rounded-full bg-red-500 flex items-center justify-center text-white text-sm font-medium ${
                                  currentUserProfile?.profileImage && isImageAccessible(currentUserProfile.profileImage) ? 'hidden' : 'block'
                                }`}
                              >
                                {currentUserProfile?.name?.charAt(0) || 'U'}
                              </div>
                            </div>
                          ) : (
                            // Other users' messages - show their profile picture if available, otherwise fallback initial
                            <div className="relative">
                              {otherUserProfileImage && isImageAccessible(otherUserProfileImage) ? (
                                <img
                                  src={otherUserProfileImage}
                                  alt={authorName || 'User'}
                                  className="w-8 h-8 rounded-full object-cover"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-8 h-8 rounded-full bg-gray-500 flex items-center justify-center text-white text-sm font-medium ${otherUserProfileImage && isImageAccessible(otherUserProfileImage) ? 'hidden' : 'block'}`}>
                                {(authorName || 'U').charAt(0)}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className={`flex-1 ${isCurrentUser ? 'text-right' : ''}`}>
                          <div className={`flex items-center gap-2 mb-1 ${isCurrentUser ? 'justify-end' : ''}`}>
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {authorName || 'User'}
                            </span>
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {note.createdAt ? new Date(note.createdAt).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : 'N/A'}
                            </span>
                          </div>
                          {editingNote === index ? (
                            <div className="space-y-2">
                              <textarea
                                value={editedNoteText}
                                onChange={(e) => setEditedNoteText(e.target.value)}
                                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                                rows="3"
                              />
                              <div className="flex gap-2">
                                <button
                                  onClick={() => saveNote(index)}
                                  className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white text-sm rounded"
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelNoteEdit}
                                  className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className={`inline-block p-3 rounded-lg ${
                              isCurrentUser 
                                ? 'bg-red-600 text-white' 
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100'
                            }`}>
                              <p className="whitespace-pre-wrap">{noteText}</p>
                              {isCurrentUser && (
                                <div className="flex items-center gap-2 mt-2">
                                  <button
                                    onClick={() => editNote(index)}
                                    className="p-1 text-white hover:text-gray-200"
                                    title="Edit note"
                                  >
                                    <FaEdit className="w-3 h-3" />
                                  </button>
                                  <button
                                    onClick={() => deleteNote(index)}
                                    className="p-1 text-white hover:text-gray-200"
                                    title="Delete note"
                                  >
                                    <FaTrash className="w-3 h-3" />
                                  </button>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* New Message Form */}
            <div className="p-6 border-t border-gray-200 dark:border-gray-700">
              <form onSubmit={(e) => { e.preventDefault(); addNote(); }} className="flex gap-3">
                <div className="flex-1">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Type your message..."
                    className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 resize-none"
                    rows="3"
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    type="button"
                    className="p-3 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    title="Attach file (coming soon)"
                  >
                    <FaPaperclip className="h-5 w-5" />
                  </button>
                  <button
                    type="submit"
                    disabled={!newNote.trim() || sendingMessage}
                    className="p-3 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-lg transition-colors"
                    title="Send message"
                  >
                    {sendingMessage ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    ) : (
                      <FaPaperPlane className="h-5 w-5" />
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default FireOverview; 